/**
 * HighestEarningCourses — money-motivated discovery (2026-02-29).
 *
 * Ranks UK undergraduate courses by real HESA LEO3 (Longitudinal
 * Education Outcomes) 3-year graduate salary. A student sets a salary
 * floor and optionally a subject; we return the ranked list with
 * sample sizes and continuation rates so the signal is transparent.
 *
 * Data source: HESA Discover Uni 2024/25 (CC-BY 4.0).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCurrency } from "@/context/CurrencyContext";
import {
  TrendingUp, GraduationCap, Search, Sparkles, ArrowRight, MapPin,
  BadgePoundSterling, Info,
} from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import EmployerProgressionCard from "@/components/app/EmployerProgressionCard";
import SalaryRangeBand from "@/components/app/degree/SalaryRangeBand";

const SALARY_BANDS = [
  { gbp: 30000, label: "£30K+" },
  { gbp: 35000, label: "£35K+" },
  { gbp: 40000, label: "£40K+" },
  { gbp: 45000, label: "£45K+" },
  { gbp: 50000, label: "£50K+" },
  { gbp: 60000, label: "£60K+" },
];

export default function HighestEarningCourses() {
  const { formatFrom } = useCurrency();
  const [minSalary, setMinSalary] = useState(45000);
  const [degreeQuery, setDegreeQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sortBy, setSortBy] = useState("leo3"); // leo3 | leo5 | lift
  const [country, setCountry] = useState("UK"); // UK | US — switches datasets
  const [usSubject, setUsSubject] = useState(""); // "" | STEM | Business | ...
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(degreeQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [degreeQuery]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        // Country-specific dataset:
        //  - UK -> HESA LEO 3y/5y course-level medians.
        //  - US -> College Scorecard 10-yr uni-level earnings.
        // Both endpoints return `{ courses: [...] }` in the same
        // structural shape so the row template renders either.
        const url = country === "US"
          ? "/degrees/highest-earning-us"
          : "/degrees/highest-earning";
        const params = country === "US"
          ? { min_salary_usd: usSubject ? 30000 : 60000, uni_query: debouncedQuery, horizon: "10yr", subject_area: usSubject, limit: 60 }
          : { min_salary_gbp: minSalary, degree_query: debouncedQuery, sort_by: sortBy, limit: 60 };
        const { data } = await api.get(url, { params });
        if (!cancelled) setResults(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [minSalary, debouncedQuery, sortBy, country, usSubject]);

  // Currency symbol keeps the row template country-agnostic.
  const isUs = country === "US";
  const currencySymbol = isUs ? "$" : "£";
  const fmtGbp = (n) => {
    if (n == null) return "—";
    if (isUs) return `$${Math.round(n).toLocaleString()}`;
    return formatFrom(n, "GBP", { decimals: 0 });
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <SEOHead
        title="Highest-Earning UK Degrees | Real 3-Year Salary Data | WhichUniversityAdvisor"
        description="Real HESA Longitudinal Education Outcomes: UK undergraduate courses ranked by median graduate salary 3 years after finishing. Filter by degree + salary floor."
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-6">
        <Link to="/explore" data-testid="highest-earning-back" className="text-xs uppercase tracking-wider text-stone-500 hover:text-[#002147] inline-flex items-center gap-1.5">
          ← Explore
        </Link>
        <div className="flex items-center gap-2 mt-4 text-xs uppercase tracking-widest text-[#7C3AED] font-semibold">
          <BadgePoundSterling className="h-3.5 w-3.5" />{" "}
          {isUs
            ? "US Scorecard · Median earnings 10yr · Public domain"
            : "HESA LEO3 · 2024/25 · CC-BY 4.0"}
        </div>
        <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tight text-[#002147]">
          {isUs ? (
            <>
              US universities where grads<br />
              <span className="text-[#7C3AED]">earn most</span> a decade later.
            </>
          ) : (
            <>
              UK courses where grads earn<br />
              <span className="text-[#7C3AED]">£{minSalary.toLocaleString()}+</span> three years out.
            </>
          )}
        </h1>
        <p className="mt-4 text-stone-600 max-w-2xl text-base">
          {isUs
            ? "US College Scorecard median 10-yr post-entry earnings, straight from the Department of Education's public dataset. Every institution ranked by what its graduates actually earn a decade after they started — no estimates, no marketing."
            : "Real HESA Longitudinal Education Outcomes data. No estimates, no marketing brochures — the actual median tax-return earnings of every UK undergraduate 3 years after graduation, with sample sizes shown so you can gauge how trustworthy each number is."}
        </p>
      </section>

      {/* Filter bar */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-6">
        <div className="bg-white border border-stone-200 rounded-xl p-5 lg:p-6 shadow-sm" data-testid="highest-earning-filters">
          {/* Country toggle — swaps the entire dataset. UK uses HESA
              LEO (course-level, 3y/5y medians); US uses College
              Scorecard (uni-level, 10y earnings). Kept at the top of
              the filter bar so users pick their country BEFORE
              tweaking any other axis. */}
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">
            <MapPin className="h-3.5 w-3.5 text-[#7C3AED]" /> Country
          </div>
          <div
            className="flex flex-wrap gap-2 mb-4"
            data-testid="highest-earning-country-toggle"
          >
            {[
              { key: "UK", label: "🇬🇧 UK · HESA LEO (course-level)" },
              { key: "US", label: "🇺🇸 US · Scorecard 10yr (uni-level)" },
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => { setUsSubject(""); setCountry(c.key); }}
                data-testid={`highest-earning-country-${c.key.toLowerCase()}`}
                aria-pressed={country === c.key}
                className={`px-4 py-2 text-sm rounded-full border transition font-medium ${
                  country === c.key
                    ? "bg-[#4C1D95] text-white border-[#4C1D95] shadow-sm"
                    : "bg-white text-stone-700 border-stone-200 hover:border-[#4C1D95] hover:text-[#4C1D95]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* US-only subject filter — switches Scorecard uni ranking
              to editorial per-programme ranking within a field of
              study. Sample is small (~54 rows across all fields today)
              but subject-specific signal beats a generic uni number
              when a family already knows what their child wants to
              study. Hidden in UK mode because the UK page already
              uses degree-level LEO data with a full subject search. */}
          {isUs && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">
                <GraduationCap className="h-3.5 w-3.5 text-[#7C3AED]" /> Field of study (US)
              </div>
              <div className="flex flex-wrap gap-2" data-testid="highest-earning-us-subject-toggle">
                {[
                  { key: "",                 label: "All fields (Scorecard uni-level)" },
                  { key: "STEM",             label: "STEM" },
                  { key: "Business",         label: "Business" },
                  { key: "Social Sciences",  label: "Social Sciences" },
                  { key: "Humanities",       label: "Humanities" },
                  { key: "Life Sciences",    label: "Life Sciences" },
                  { key: "Medicine",         label: "Medicine" },
                ].map((s) => (
                  <button
                    key={s.key || "all"}
                    type="button"
                    onClick={() => setUsSubject(s.key)}
                    data-testid={`highest-earning-us-subject-${(s.key || "all").toLowerCase().replace(/\s+/g, "-")}`}
                    aria-pressed={usSubject === s.key}
                    className={`px-3 py-1.5 text-xs rounded-full border transition font-medium ${
                      usSubject === s.key
                        ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                        : "bg-white text-stone-700 border-stone-200 hover:border-[#4C1D95] hover:text-[#4C1D95]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {usSubject && (
                <div className="mt-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 max-w-2xl">
                  Field-of-study rankings use the College Scorecard's
                  federal per-programme (CIP 4-digit) median earnings
                  1 year after graduation — 24k+ real programmes across
                  1.6k US unis. Public domain federal data.
                </div>
              )}
            </div>
          )}
          {/* Salary bands only apply to the UK dataset; the US
              Scorecard endpoint uses a fixed USD floor internally so
              we hide the GBP band picker in US mode to avoid confusion. */}
          {!isUs && (
            <>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">
                <TrendingUp className="h-3.5 w-3.5 text-[#7C3AED]" /> Salary floor
              </div>
              <div className="flex flex-wrap gap-2">
            {SALARY_BANDS.map((b) => (
              <button
                key={b.gbp}
                onClick={() => setMinSalary(b.gbp)}
                data-testid={`salary-band-${b.gbp}`}
                className={`px-4 py-2 text-sm rounded-full border transition font-medium ${
                  minSalary === b.gbp
                    ? "bg-[#7C3AED] text-white border-[#7C3AED] shadow-sm"
                    : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
            </>
          )}
          <div className="mt-5 border-t border-stone-100 pt-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">
              <GraduationCap className="h-3.5 w-3.5 text-[#7C3AED]" /> {isUs ? "University (optional)" : "Subject (optional)"}
            </div>
            <div className="relative max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                data-testid="highest-earning-degree-input"
                value={degreeQuery}
                onChange={(e) => setDegreeQuery(e.target.value)}
                placeholder={isUs
                  ? "e.g. MIT, Stanford, Harvard, State University"
                  : "e.g. Computer Science, Medicine, Economics, Engineering"}
                className="pl-9"
              />
            </div>
          </div>
          {/* Sort toggle only applies to the UK dataset — Scorecard
              gives us one snapshot per uni, so there's no lift or
              5-yr axis to switch to. Hidden in US mode to avoid
              rendering a toggle that does nothing. */}
          {!isUs && (
            <div className="mt-5 border-t border-stone-100 pt-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">
                <TrendingUp className="h-3.5 w-3.5 text-[#7C3AED]" /> Rank by
              </div>
              <div className="flex flex-wrap gap-2" data-testid="highest-earning-sort-toggle">
                {[
                  { key: "leo3", label: "3-yr salary", hint: "Absolute median 3 years out" },
                  { key: "leo5", label: "5-yr salary", hint: "Absolute median 5 years out" },
                  { key: "lift", label: "3y→5y lift", hint: "Fastest career growth (progression)" },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSortBy(s.key)}
                    title={s.hint}
                    data-testid={`highest-earning-sort-${s.key}`}
                    aria-pressed={sortBy === s.key}
                    className={`px-4 py-2 text-sm rounded-full border transition font-medium ${
                      sortBy === s.key
                        ? "bg-[#4C1D95] text-white border-[#4C1D95] shadow-sm"
                        : "bg-white text-stone-700 border-stone-200 hover:border-[#4C1D95] hover:text-[#4C1D95]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-editorial text-2xl text-[#002147]">
            {loading ? "Ranking…" : `${results?.returned_count ?? 0} courses`}
          </h2>
          <span className="text-xs text-stone-500 font-mono" data-testid="highest-earning-source">
            {results?.source ?? ""}
          </span>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm mb-4" data-testid="highest-earning-error">
            {err}
          </div>
        )}

        {loading && (
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-stone-200 h-24 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && results && results.courses.length === 0 && (
          <div className="bg-white border border-stone-200 rounded-xl p-10 text-center" data-testid="highest-earning-empty">
            <p className="text-stone-600">
              {isUs
                ? "No US universities match this filter. Try removing the search query."
                : `No UK courses have HESA LEO3 data at £${minSalary.toLocaleString()}+ for this subject. Try lowering the salary floor or removing the subject filter.`}
            </p>
          </div>
        )}

        {!loading && results && results.courses.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100 overflow-hidden" data-testid="highest-earning-results">
            {(() => {
              // Compute a shared salary scale across the visible rows so
              // every SalaryRangeBand renders on the same x-axis — a
              // "wide" bar in row #7 is genuinely wider than the P25→P75
              // spread in row #1, not just a per-row artefact. We pick
              // the LEO field that matches the active sort so the axis
              // stays intuitive as the user flips modes.
              const bandField = sortBy === "leo5" ? "leo5" : "leo3";
              const lowers = results.courses.map((c) => c[`${bandField}_lower_gbp`]).filter(Boolean);
              const uppers = results.courses.map((c) => c[`${bandField}_upper_gbp`]).filter(Boolean);
              const scaleMin = lowers.length ? Math.min(...lowers) : null;
              const scaleMax = uppers.length ? Math.max(...uppers) : null;
              return results.courses.map((c, idx) => (
              <div
                key={c.degree_id}
                className="p-5 hover:bg-stone-50 transition group"
                data-testid={`he-row-${c.degree_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Rank */}
                  <div className="shrink-0 h-10 w-10 rounded-lg bg-[#F5F0FF] flex items-center justify-center font-mono text-sm font-semibold text-[#7C3AED]">
                    {idx + 1}
                  </div>

                  {/* Course + Uni */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/degree/${c.degree_id}`}
                      className="font-editorial text-lg text-[#002147] leading-tight hover:text-[#7C3AED] hover:underline decoration-2 underline-offset-2 transition inline-block"
                      data-testid={`he-title-link-${c.degree_id}`}
                    >
                      {c.title}
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-500">
                      {(c.qualification || c.duration_years) && (
                        <>
                          <span>
                            {c.qualification}
                            {c.duration_years && ` · ${c.duration_years} yr`}
                          </span>
                          <span>·</span>
                        </>
                      )}
                      <Link to={c.university.id ? `/uni/${c.university.id}` : "#"}
                            className="text-[#4C1D95] hover:underline flex items-center gap-1"
                            data-testid={`he-uni-link-${c.degree_id}`}>
                        <MapPin className="h-3 w-3" /> {c.university.name}
                      </Link>
                      {c.university.city && (
                        <>
                          <span>·</span>
                          <span>{c.university.city}</span>
                        </>
                      )}
                    </div>

                    {/* Signals row */}
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-500 font-underground-sc tracking-wider">
                      {c.continuation_rate != null && (
                        <span data-testid={`he-cont-${c.degree_id}`}>
                          {Math.round(c.continuation_rate * 100)}% continue
                        </span>
                      )}
                      {c.nss_avg != null && c.nss_sample_size >= 30 && (
                        <span data-testid={`he-nss-${c.degree_id}`}>
                          NSS {c.nss_avg}
                        </span>
                      )}
                      {c.salary_median_gbp != null && c.salary_sample_size >= 30 && (
                        <span data-testid={`he-yr1-${c.degree_id}`}>
                          Yr-1 {fmtGbp(c.salary_median_gbp)}
                        </span>
                      )}
                    </div>

                    {/* IQR band — same LEO waypoint as the ranked column
                        (3y in default & lift modes, 5y in leo5 mode)
                        so the visual matches the headline number. Hidden
                        in US mode because Scorecard gives us a single
                        median per uni with no IQR breakdown. */}
                    {!isUs && (
                      <div className="mt-3 max-w-md">
                        <SalaryRangeBand
                          lower_gbp={sortBy === "leo5" ? c.leo5_lower_gbp : c.leo3_lower_gbp}
                          upper_gbp={sortBy === "leo5" ? c.leo5_upper_gbp : c.leo3_upper_gbp}
                          median_gbp={sortBy === "leo5" ? c.leo5_median_gbp : c.leo3_median_gbp}
                          scale_min_gbp={scaleMin}
                          scale_max_gbp={scaleMax}
                          currency_symbol="£"
                          testid={`he-iqr-${c.degree_id}`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Salary */}
                  <div className="shrink-0 text-right">
                    {/* Salary column adapts to the active sort axis so
                        the ranked field is always the prominent one.
                        For "lift" mode we swap in a compact 3y → 5y
                        trajectory with the % lift as the headline. */}
                    {sortBy === "lift" && c.lift_pct != null ? (
                      <>
                        <div className="font-mono font-semibold text-[#7C3AED] text-xl leading-tight" data-testid={`he-lift-${c.degree_id}`}>
                          +{c.lift_pct}%
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-0.5">
                          3y → 5y lift
                        </div>
                        <div className="text-[11px] font-mono text-stone-600 mt-1">
                          {fmtGbp(c.leo3_median_gbp)} <span className="text-stone-400">→</span> {fmtGbp(c.leo5_median_gbp)}
                        </div>
                      </>
                    ) : sortBy === "leo5" && c.leo5_median_gbp ? (
                      <>
                        <div className="font-mono font-semibold text-[#002147] text-xl leading-tight" data-testid={`he-salary-${c.degree_id}`}>
                          {fmtGbp(c.leo5_median_gbp)}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-0.5">
                          5-YR MEDIAN · n={c.leo5_sample_size ?? "—"}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-mono font-semibold text-[#002147] text-xl leading-tight" data-testid={`he-salary-${c.degree_id}`}>
                          {fmtGbp(c.leo3_median_gbp)}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-0.5">
                          {isUs
                            ? "10-YR MEDIAN · US SCORECARD"
                            : `3-YR MEDIAN · n=${c.leo3_sample_size}`}
                        </div>
                      </>
                    )}
                    <Link
                      to={`/degree/${c.degree_id}`}
                      data-testid={`he-cta-${c.degree_id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-[#7C3AED] hover:underline mt-2"
                    >
                      See course <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </div>
              ));
            })()}
          </div>
        )}

        {/* Employer progression rankings — sits below the course list
            because the question "which firm should I aim for?" is the
            natural follow-up once families see the courses that hit
            £45K+ at 3 years. Follows the active country toggle so US
            mode shows Google/Goldman/Meta instead of Magic Circle law. */}
        <div className="mt-10">
          <EmployerProgressionCard
            limit={12}
            minAppearances={3}
            country={country}
          />
        </div>

        {/* Method */}
        <div className="mt-8 bg-white/70 border border-stone-200 rounded-lg p-5 text-xs text-stone-600 leading-relaxed" data-testid="highest-earning-methodology">
          <div className="font-semibold text-stone-700 mb-1 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" /> How this works
          </div>
          <p>
            <strong>LEO3</strong> is the Department for Education's Longitudinal Education Outcomes dataset — the actual HMRC-reported earnings of every UK-domiciled graduate three tax years after finishing, matched to the course they studied. It's the most honest earnings signal available for UK degrees.
            We rank by <strong>median</strong> earnings (not mean — a few finance-bro outliers shouldn't distort a course), require a minimum sample of {results?.min_sample ?? 20} students, and de-duplicate near-identical KIS variants for the same course.
            The <strong>IQR</strong> band shows the lower and upper quartile — so you can see whether a "£45K median" means everyone earns £45K, or half earn £30K and half earn £60K.
            Data: HESA Discover Uni 2024/25 (CC-BY 4.0).
          </p>
        </div>
      </section>
    </div>
  );
}
